import { Injectable } from '@angular/core';
import {
  PartnerSalesService,
  SalesConfigInputsInterface,
  SalesConfigurationInterface,
  SalesSetting,
  SetSalesConfigResponseInterface,
} from '@galaxy/partner';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OrderSetupService {
  constructor(private readonly partnerSalesService: PartnerSalesService) {}
  private saveOrderConfigState = new ObservableWorkStateMap<string, SetSalesConfigResponseInterface>();
  private readonly getOrderConfigState = new ObservableWorkStateMap<string, SalesConfigurationInterface>();

  private static convertBoolToSetting(value: boolean): SalesSetting {
    return value ? SalesSetting.SALES_SETTING_ENABLED : SalesSetting.SALES_SETTING_DISABLED;
  }

  private static constructWorkStateMapId(partnerId: string, marketId: string): string {
    return marketId ? `${partnerId}-${marketId}` : partnerId;
  }

  public saveSalesOrderConfig(partnerId: string, marketId: string, newConfig: SalesConfigurationInterface): void {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    const configInputs = this.convertConfigToSetSalesConfigInput(newConfig);
    this.saveOrderConfigState.startWork(
      ID,
      this.partnerSalesService.setConfiguration(partnerId, marketId, configInputs),
    );
  }

  public getSalesOrderConfig(partnerId: string, marketId?: string): void {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    this.getOrderConfigState.startWork(
      ID,
      this.partnerSalesService.getConfiguration(partnerId, marketId).pipe(map((resp) => resp.config)),
    );
  }

  public getSalesOrderConfigWorkResults(partnerId: string, marketId: string): Observable<SalesConfigurationInterface> {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    return this.getOrderConfigState.getWorkResults$(ID);
  }

  public isGetOrderConfigReady(partnerId: string, marketId?: string): Observable<boolean> {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    return combineLatest([this.getOrderConfigState.isLoading$(ID), this.getOrderConfigState.isSuccess$(ID)]).pipe(
      map(([isLoading, isSuccess]) => isLoading === false && isSuccess === true),
    );
  }

  public isSaveOrderConfigLoading(partnerId: string, marketId: string): Observable<boolean> {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    return this.saveOrderConfigState.isLoading$(ID);
  }

  public isSaveOrderConfigSuccessful(partnerId: string, marketId: string): Observable<boolean> {
    const ID = OrderSetupService.constructWorkStateMapId(partnerId, marketId);
    return this.saveOrderConfigState.successEvent$(ID);
  }
  private convertConfigToSetSalesConfigInput(getConfig: SalesConfigurationInterface): SalesConfigInputsInterface {
    const inputInterface: SalesConfigInputsInterface = {};
    inputInterface.hideProducts = OrderSetupService.convertBoolToSetting(getConfig.hideProducts);
    inputInterface.hideRmDemo = OrderSetupService.convertBoolToSetting(getConfig.hideRmDemo);
    inputInterface.hideOrders = OrderSetupService.convertBoolToSetting(getConfig.hideOrders);
    inputInterface.stTeamActivity = OrderSetupService.convertBoolToSetting(getConfig.stTeamActivity);
    inputInterface.stAllowSalespersonCampaignAdd = OrderSetupService.convertBoolToSetting(
      getConfig.stAllowSalespersonCampaignAdd,
    );
    inputInterface.stShowProductsWholesalePrice = OrderSetupService.convertBoolToSetting(
      getConfig.stShowProductsWholesalePrice,
    );
    inputInterface.stShowTrainingResourcesField = OrderSetupService.convertBoolToSetting(
      getConfig.stShowTrainingResourcesField,
    );
    inputInterface.stSalespersonSharedAccountAccess = OrderSetupService.convertBoolToSetting(
      getConfig.stSalespersonSharedAccountAccess,
    );
    inputInterface.stDefaultDisplayCurrency = getConfig.stDefaultDisplayCurrency;
    inputInterface.stEnableSnapshotsPerSalespersonLimit = OrderSetupService.convertBoolToSetting(
      getConfig.stEnableSnapshotsPerSalespersonLimit,
    );
    inputInterface.stSnapshotsPerSalespersonLimit = getConfig.stSnapshotsPerSalespersonLimit;
    inputInterface.stBusinessCenterAccess = OrderSetupService.convertBoolToSetting(getConfig.stBusinessCenterAccess);
    return inputInterface;
  }
}
